import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FileText, Download, ShieldCheck, PenLine, Stamp, ArrowRight, Search, Loader2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Resources() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/affidavits`)
      .then((res) => setTemplates(res.data.templates || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const steps = [
    { icon: Download, t: "Download", d: "Pick the affidavit for your sector code and entity type." },
    { icon: PenLine, t: "Complete", d: "Fill in your company details, revenue and B-BBEE level." },
    { icon: Stamp, t: "Commission", d: "Sign it before a Commissioner of Oaths — and you're compliant." },
  ];

  // Group templates by sector, preserving backend order (Generic first).
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = templates.filter(
      (t) =>
        !q ||
        t.sector.toLowerCase().includes(q) ||
        t.code_name.toLowerCase().includes(q) ||
        t.tag.toLowerCase().includes(q) ||
        t.subtitle.toLowerCase().includes(q)
    );
    const map = new Map();
    filtered.forEach((t) => {
      if (!map.has(t.sector)) map.set(t.sector, { sector: t.sector, code_name: t.code_name, items: [] });
      map.get(t.sector).items.push(t);
    });
    return Array.from(map.values());
  }, [templates, query]);

  const tagColor = (tag) =>
    tag.startsWith("Specialised")
      ? "bg-[#0B1F3A] text-[#D4AF37]"
      : tag === "EME"
      ? "bg-[#F6F7F9] text-[#184E9E]"
      : "bg-[#EEF3FB] text-[#184E9E]";

  return (
    <div>
      <PageHeader
        title="Free B-BBEE Resources"
        crumb="Resources"
        subtitle="Download ready-to-use B-BBEE sworn-affidavit templates for every sector code — professionally branded and free to use for your compliance."
      />

      {/* How it works */}
      <section className="py-14 bg-[#F6F7F9]">
        <div className="container-x grid sm:grid-cols-3 gap-5">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.t} delay={idx * 80}>
                <div className="flex gap-3 items-start bg-white rounded-xl border border-slate-100 p-5 h-full">
                  <div className="w-10 h-10 rounded-lg bg-[#0B1F3A] grid place-items-center text-[#D4AF37] shrink-0">
                    <Icon size={20} />
                  </div>
                  <div>
                    <h4 className="font-head font-semibold text-[#0B1F3A] text-[15px]">{idx + 1}. {s.t}</h4>
                    <p className="text-sm text-slate-500">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Library */}
      <section className="py-16">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10">
            <div>
              <div className="eyebrow mb-3">Affidavit Library</div>
              <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A]">Sworn Affidavits by Sector Code</h2>
              <p className="mt-2 text-slate-600 max-w-2xl">
                Generic and sector-specific EME, QSE and Specialised Entity affidavits — each generated with the BEE Exsellance watermark, ready to complete and commission.
              </p>
            </div>
            <div className="relative w-full md:w-80">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sector, e.g. Financial, MAC, QSE…"
                className="w-full rounded-lg border border-slate-200 pl-10 pr-3.5 py-2.5 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-3 text-slate-500 py-16 justify-center">
              <Loader2 className="animate-spin" size={22} /> Loading affidavit library…
            </div>
          ) : groups.length === 0 ? (
            <p className="text-center text-slate-500 py-16">No affidavits match “{query}”.</p>
          ) : (
            <div className="space-y-12">
              {groups.map((g) => (
                <div key={g.sector}>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="w-1.5 h-6 rounded bg-[#D4AF37]" />
                    <h3 className="font-head font-bold text-xl text-[#0B1F3A]">{g.sector}</h3>
                    <span className="text-sm text-slate-400 hidden sm:block">· {g.code_name}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {g.items.map((a) => (
                      <div key={a.kind} className="group card-lift bg-white rounded-2xl border border-slate-100 p-6 h-full flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 rounded-xl bg-[#0B1F3A] grid place-items-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0B1F3A] transition-colors">
                            <FileText size={22} />
                          </div>
                          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${tagColor(a.tag)}`}>{a.tag}</span>
                        </div>
                        <h4 className="font-head font-semibold text-[#0B1F3A] text-[15px] mb-1 leading-snug">
                          {a.tag} Affidavit
                        </h4>
                        <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{a.subtitle}</p>
                        <a
                          href={`${API}/affidavits/${a.kind}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-gold mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm"
                        >
                          <Download size={16} /> Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer + CTA */}
      <section className="pb-20">
        <div className="container-x">
          <div className="rounded-2xl bg-[#0B1F3A] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-12 h-12 rounded-xl bg-white/10 grid place-items-center text-[#D4AF37] shrink-0">
              <ShieldCheck size={24} />
            </div>
            <p className="text-slate-300 text-sm leading-relaxed flex-1">
              These templates are provided as a free courtesy to support your B-BBEE compliance. They are
              not a substitute for professional advice — if you're unsure which sector code or affidavit
              applies, or how to determine your level, our specialists are here to help.
            </p>
            <Link to="/contact" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-lg whitespace-nowrap">
              Get Expert Help <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
